package com.crowpay.views.dialogs

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.SignalingList
import com.crowpay.utils.renderDollars
import com.crowpay.views.components.project.work.changeOrders.*
import com.crowpay.views.components.project.work.payApps.LineItemWithPaymentInfo
import com.crowpay.views.components.requiredField
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.ThemeDerivation
import com.lightningkite.kiteui.models.div
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*

class PagedUserTextInputDialog(
    val header: String,
    val message: String,
    val details: List<String>,
    val confirmationText: String,
    val confirmationTheme: ReactiveContext.(isLast: Boolean) -> ThemeDerivation = { DangerButtonSemantic },
    val dismissText: String = "Go Back",
    val minChars: Int = 35,
    val denyWithFeedback: suspend (String) -> Unit
) : Page {
    private val reasons = SignalingList<String>()
    private val reason = Property("")

    override fun ViewWriter.render2(): ViewModifiable = dismissBackground {
        spacing = AppDimensions.fullIndent
        dialog - centered - stack {
            spacing = AppDimensions.fullIndent
            removeOutline - col {
                title(header)

                body(message)

                body {
                    ::content {
                        details.elementAtOrNull(reasons().size) ?: ""
                    }
                }

                col {
                    spacing = theme.spacing / 2
                    requiredField("Reason") {
                        sizeConstraints(height = 8.rem) - textArea {
                            if (minChars > 0) {
                                hint = "Minimum $minChars characters"
                            }
                            content bind reason
                        }
                    }
                    row {
                        spacing = 0.rem
                        atTopCenter - expanding - smallBody {
                            ::content {
                                "item ${reasons().size + 1} of ${details.size}"
                            }
                        }
                        atBottomCenter - smallBody {
                            ::content {
                                val remaining = (minChars - reason().length).coerceAtLeast(0)
                                if (remaining > 0) "$remaining remaining"
                                else ""
                            }
                        }
                    }
                }

                space(0.5)

                row {
                    button {
                        dynamicTheme {
                            confirmationTheme(reasons().size == details.size - 1)
                        }
                        ::enabled { reason().isNotBlank() }
                        specCenteredText {
                            if (reasons().size == details.size - 1) confirmationText else "Next Item"
                        }
                        onClick(confirmationText) {
                            reasons.add(reason())
                            denyWithFeedback(reason())
                            if (reasons().size == details.size) {
                                dialogScreenNavigator.dismiss()
                            } else reason.set("")
                        }
                    }
                    secondaryButton - button {
                        specCenteredText(dismissText)
                        onClick(dismissText) {
                            reasons.add(reason())
                            denyWithFeedback("")
                            if (reasons().size == details.size) {
                                dialogScreenNavigator.dismiss()
                            }
                        }
                    }
                }
            }
        }
    }
}

fun DenyChangeItemsDialog(
    items: List<ChangeRequestItem>,
    project: UUID,
    denial: suspend (List<Pair<ChangeRequestItem, String>>) -> Unit
): PagedUserTextInputDialog {
    fun ChangeRequestItem.name(quotes: Boolean = false): String? = when (this) {
        is ChangeRequestItemWrapper -> wrapped.name()
        is PendingLineItem -> if (quotes) "\"$name\"" else name
        else -> itemNumber
    }

    if (items.isEmpty()) throw IllegalArgumentException("list cannot be empty")

    val linkedItems = items.filter { it.linked }
    val nonLinkedItems = items.filter { !it.linked }
    val details = mutableListOf<String>()
    val orderedItems = mutableListOf<List<ChangeRequestItem>>()
    if (linkedItems.isNotEmpty()) {
        details.add(
            "Linked Items, ${linkedItems.joinToString(", ") { it.name(false) ?: it.itemNumber }} (${
                linkedItems.sumOf { it.priceChange ?: 0 }.renderDollars()
            })"
        )
        orderedItems.add(linkedItems)
    }
    nonLinkedItems.forEachIndexed { index, item ->
        details.add("Change Order Item ${item.name(false)} (${item.priceChange.renderDollars()})")
        orderedItems.add(listOf(item))
    }

    var index = 0

    return PagedUserTextInputDialog(
        header =
            if (items.size == 1) "Deny ${items.first().name()}?"
            else "Deny ${items.size} Change Items?",
        message = "Are you sure you want to deny these change items? If you're sure, let the contractor know why below.",
        details = details,
        confirmationText =
            if (items.size == 1) "Deny ${items.first().name()}"
            else "Deny Items",

    ) { feedback ->
        val itemList = orderedItems[index]
        index += 1
        if (feedback.isNotBlank()) {
            denial(itemList.map {
                Pair(it, feedback)
            })
        }
    }
}

//        val session = notNullSession()
//        session.projectMessages.insert(
//            feedbackList.mapIndexed { index, feedback ->
//                ProjectMessage(
//                    project = project,
//                    sender = session.userId,
//                    senderName = session.self().fullName,
//                    clientMessage = true,
//                    message = feedback,
//                    referencesChangeRequest = ChangeRequestMessageReference(
//                        id = items.elementAtOrNull(index)?.changeRequest ?: items.first().changeRequest,
//                        reason = buildString {
//                            append("Denied change item ")
//                            fun ChangeRequestItem.title(): String = when (this) {
//                                is ChangeRequestItemWrapper -> wrapped.title()
//                                is PendingLineItem -> "\"$name\" ($itemNumber)"
//                                else -> itemNumber
//                            }
//                            append(items.first().title())
//                        }
//                    )
//                )
//            }
//        )

fun WaitDrawsDialog(
    draws: List<LineItemWithPaymentInfo>,
    header: String,
    onSubmit: suspend (UUID) -> Unit,
): PagedUserTextInputDialog {
    var index = 0
    val items = draws.filter { it.payApp != null }
    if (items.isEmpty()) throw IllegalArgumentException("Cannot have wait an empty list of pay app items")

    suspend fun submit(value: UUID) {
        onSubmit(value)
    }

    return PagedUserTextInputDialog(
        header = header,
        message = "Are you sure you want to wait on paying this draw item? If you're sure, let the contractor know why below.",
        details = items.map { "${it.name}, ${it.payApp?.amount}" },
        confirmationText = "Wait",
        dismissText = "Cancel"
    ) { feedback ->
        val session = notNullSession()

        val item = items[index]
        index += 1

        if (feedback.isNotBlank()) {
            session.nonCached.payAppItem.denyPayApp(
                id = item.payApp?._id ?: UUID.random(),
                input = feedback
            )
            submit(item.lineItem._id)
        }

//        session.projectMessages.insert(
//            feedbackList.mapIndexed { index, feedback ->
//                ProjectMessage(
//                    project = items.elementAtOrNull(index)?.lineItem?.project ?: items.first().lineItem.project,
//                    sender = session.userId,
//                    senderName = session.self().fullName,
//                    clientMessage = true,
//                    message = feedback,
//                    referencesPayApp = PayAppMessageReference(
//                        id = items.elementAtOrNull(index)?.lineItem?.project ?: items.first().lineItem.project,
//                        reason = buildString {
//                            val item = items.elementAtOrNull(index) ?: items.first()
//                            append("Waited Draw Item ${item.lineItem.name} (${item.payApp?.amount.renderDollars()}), on $drawTitle")
//                        }
//                    )
//                )
//            }
//        )
    }
}